// Copyright (c) 2026 Watch Tower Bible and Tract Society of Pennsylvania
// All Rights Reserved. Proprietary and confidential.
// Unauthorized copying, modification, or distribution is strictly prohibited.
import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Send, Loader2, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import Layout from '../components/Layout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AUDITE_AVATAR = '/audite-avatar.png';

export default function AssistantPage() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => { scrollToBottom(); }, [messages]);
  useEffect(() => { inputRef.current?.focus(); loadHistory(); }, []); // eslint-disable-line

  const loadHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/assistant/history`);
      if (data.messages?.length > 0) {
        setMessages(data.messages.map(m => ({ role: m.role, content: m.content })));
        if (data.session_id) setSessionId(data.session_id);
      }
    } catch (e) {
      // History endpoint may legitimately return 404 if the user has no prior session.
    }
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;

    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setLoading(true);

    try {
      const { data } = await axios.post(`${API}/assistant/chat`, {
        message: text,
        session_id: sessionId,
      });
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      if (data.session_id) setSessionId(data.session_id);
    } catch (e) {
      console.error('Assistant chat error:', e);
      setMessages(prev => [...prev, { role: 'assistant', content: 'Desculpe, ocorreu um erro. Tente novamente.' }]);
    }
    setLoading(false);
  };

  const handleClear = async () => {
    try {
      await axios.delete(`${API}/assistant/history`);
    } catch (e) {
      // Server-side history clear failed; local state is still cleared below
      // so the user still gets the visual reset they expected.
    }
    setMessages([]);
    setSessionId(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const quickQuestions = [
    'Como criar um novo relatório?',
    'Como preencher o formulário S-25b?',
    'Como instalar no celular?',
    'O que são os trimestres?',
    'Como compartilhar um relatório?',
    'Como gerenciar documentos?',
  ];

  return (
    <Layout>
      <div className="max-w-3xl mx-auto" data-testid="assistant-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <img src={AUDITE_AVATAR} alt="Audite" className="w-10 h-10 rounded-xl border border-border" />
            <div>
              <h1 className="text-xl font-bold text-foreground">Audite</h1>
              <p className="text-sm text-muted-foreground">Assistente Virtual JW Audit</p>
            </div>
          </div>
          {messages.length > 0 && (
            <Button variant="outline" size="sm" onClick={handleClear} data-testid="assistant-page-clear">
              <Trash2 className="w-4 h-4 mr-1" /> Limpar
            </Button>
          )}
        </div>

        {/* Chat area */}
        <div className="bg-card border border-border rounded-2xl overflow-hidden flex flex-col" style={{ height: 'calc(100vh - 220px)' }}>
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.length === 0 && (
              <div className="text-center py-12">
                <img src={AUDITE_AVATAR} alt="Audite" className="w-20 h-20 mx-auto rounded-full border-2 border-muted mb-4" />
                <p className="text-base font-medium text-muted-foreground mb-1">Olá! Sou a Audite</p>
                <p className="text-sm text-muted-foreground/60 mb-6">Sua assistente virtual do JW Audit. Posso ajudar com qualquer dúvida sobre o sistema!</p>
                <div className="flex flex-wrap gap-2 justify-center max-w-md mx-auto">
                  {quickQuestions.map(q => (
                    <button
                      key={q}
                      onClick={() => setInput(q)}
                      className="text-xs px-3 py-2 rounded-xl border border-border bg-muted/50 hover:bg-muted text-muted-foreground transition-colors"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((msg, i) => (
              <div key={msg.id || `msg-${i}-${msg.role}`} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} gap-2`}>
                {msg.role === 'assistant' && (
                  <img src={AUDITE_AVATAR} alt="Audite" className="w-7 h-7 rounded-lg border border-border flex-shrink-0 mt-1" />
                )}
                <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-primary text-primary-foreground rounded-br-md'
                    : 'bg-muted text-foreground rounded-bl-md'
                }`}>
                  <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start gap-2">
                <img src={AUDITE_AVATAR} alt="Audite" className="w-7 h-7 rounded-lg border border-border flex-shrink-0 mt-1" />
                <div className="bg-muted rounded-2xl rounded-bl-md px-4 py-3">
                  <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-border">
            <div className="flex gap-3">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Digite sua pergunta..."
                className="flex-1 bg-muted rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-primary/30 placeholder:text-muted-foreground/50"
                disabled={loading}
                data-testid="assistant-page-input"
              />
              <Button
                onClick={handleSend}
                disabled={!input.trim() || loading}
                size="icon"
                className="rounded-xl h-11 w-11 flex-shrink-0"
                data-testid="assistant-page-send"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
